import React, { useEffect, useMemo, useState } from "react";
import "../css/Lp2App.css";
import {
  bootloopClient,
  ListingInterestAdminRowDto,
  shoeTypeLabel,
  conditionLabel,
} from "../api/bootloopClient";

type SortKey =
  | "createdUtc"
  | "sizeUk"
  | "shoeType"
  | "condition"
  | "ownerEmail"
  | "interestedEmail";

const AdminInterests: React.FC = () => {
  const [daysBack, setDaysBack] = useState<number>(30);
  const [maxRows, setMaxRows] = useState<number>(200);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rows, setRows] = useState<ListingInterestAdminRowDto[]>([]);

  const [q, setQ] = useState<string>("");
  const [sortKey, setSortKey] = useState<SortKey>("createdUtc");
  const [sortDir, setSortDir] = useState<"desc" | "asc">("desc");

  async function load() {
    setError(null);
    try {
      setBusy(true);
      const data = await bootloopClient.getAdminInterests({ daysBack, maxRows });
      setRows(data);
    } catch (err: any) {
      setError(err?.message || "Failed to load admin interests.");
    } finally {
      setBusy(false);
    }
  }

  // initial load
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    let data = rows;

    if (term) {
      data = data.filter((r) => {
        const hay = [
          r.interestedEmail,
          r.interestedPostcode ?? "",
          r.ownerEmail,
          r.ownerPostcode ?? "",
          String(r.listingId),
          String(r.interestId),
          String(r.sizeUk),
          r.notes ?? "",
        ]
          .join(" ")
          .toLowerCase();

        return hay.includes(term);
      });
    }

    const dir = sortDir === "asc" ? 1 : -1;

    const compare = (a: ListingInterestAdminRowDto, b: ListingInterestAdminRowDto) => {
      switch (sortKey) {
        case "createdUtc":
          return (new Date(a.createdUtc).getTime() - new Date(b.createdUtc).getTime()) * dir;
        case "sizeUk":
          return (a.sizeUk - b.sizeUk) * dir;
        case "shoeType":
          return (a.shoeType - b.shoeType) * dir;
        case "condition":
          return (a.condition - b.condition) * dir;
        case "ownerEmail":
          return a.ownerEmail.localeCompare(b.ownerEmail) * dir;
        case "interestedEmail":
          return a.interestedEmail.localeCompare(b.interestedEmail) * dir;
        default:
          return 0;
      }
    };

    return [...data].sort(compare);
  }, [rows, q, sortKey, sortDir]);

  function toggleSort(key: SortKey) {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("desc");
    }
  }

  return (
    <div className="lp2app">
      <h1 className="lp2app__title">Admin · Interests</h1>
      <p className="lp2app__lead">
        Latest “Express interest” events. Filter/search to validate the loop end-to-end.
      </p>

      {error && <div className="lp2app__alert">{error}</div>}

      <div className="lp2app__card" style={{ marginBottom: 16 }}>
        <div className="lp2app__row2" style={{ alignItems: "flex-end" }}>
          <label className="lp2app__label">
            Days back
            <input
              className="lp2app__input"
              type="number"
              min={1}
              max={365}
              value={daysBack}
              onChange={(e) => setDaysBack(Number(e.target.value))}
            />
          </label>

          <label className="lp2app__label">
            Max rows
            <input
              className="lp2app__input"
              type="number"
              min={1}
              max={2000}
              value={maxRows}
              onChange={(e) => setMaxRows(Number(e.target.value))}
            />
          </label>
        </div>

        <div className="lp2app__row2" style={{ marginTop: 12, alignItems: "flex-end" }}>
          <label className="lp2app__label" style={{ flex: 1 }}>
            Filter (email / postcode / listing / notes)
            <input
              className="lp2app__input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="e.g. ss1, seed.bootloop.local, 3.5"
            />
          </label>

          <div className="lp2app__actions" style={{ justifyContent: "flex-start" }}>
            <button
              className="lp2app__btnPrimary"
              onClick={load}
              disabled={busy}
              title="Reload from the API"
            >
              {busy ? "Loading..." : "Refresh"}
            </button>
          </div>
        </div>

        <div style={{ marginTop: 12 }} className="lp2app__help">
          Showing <b>{filtered.length}</b> of <b>{rows.length}</b> rows
        </div>
      </div>

      <div className="lp2app__card" style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 980 }}>
          <thead>
            <tr>
              <Th onClick={() => toggleSort("createdUtc")} active={sortKey === "createdUtc"} dir={sortDir}>
                Created
              </Th>
              <Th>Interest</Th>
              <Th>Listing</Th>
              <Th onClick={() => toggleSort("sizeUk")} active={sortKey === "sizeUk"} dir={sortDir}>
                Size
              </Th>
              <Th onClick={() => toggleSort("shoeType")} active={sortKey === "shoeType"} dir={sortDir}>
                Type
              </Th>
              <Th onClick={() => toggleSort("condition")} active={sortKey === "condition"} dir={sortDir}>
                Condition
              </Th>
              <Th onClick={() => toggleSort("interestedEmail")} active={sortKey === "interestedEmail"} dir={sortDir}>
                Interested
              </Th>
              <Th onClick={() => toggleSort("ownerEmail")} active={sortKey === "ownerEmail"} dir={sortDir}>
                Owner
              </Th>
              <Th>Notes</Th>
              <Th>Status</Th>
            </tr>
          </thead>

          <tbody>
            {busy && (
              <tr>
                <td colSpan={10} style={{ padding: 14 }} className="lp2app__help">
                  Loading…
                </td>
              </tr>
            )}

            {!busy && filtered.length === 0 && (
              <tr>
                <td colSpan={10} style={{ padding: 14 }} className="lp2app__help">
                  No interests found for the current filter.
                </td>
              </tr>
            )}

            {filtered.map((r) => (
              <tr key={r.interestId} style={{ borderTop: "1px solid rgba(255,255,255,0.10)" }}>
                <td style={{ padding: 12, whiteSpace: "nowrap" }} className="lp2app__help">
                  {new Date(r.createdUtc).toLocaleString()}
                </td>

                <td style={{ padding: 12 }} className="lp2app__help">
                  #{r.interestId}
                </td>

                <td style={{ padding: 12 }} className="lp2app__help">
                  #{r.listingId}
                </td>

                <td style={{ padding: 12 }} className="lp2app__help">
                  UK {r.sizeUk}
                </td>

                <td style={{ padding: 12 }} className="lp2app__help">
                  {shoeTypeLabel(r.shoeType)}
                </td>

                <td style={{ padding: 12 }} className="lp2app__help">
                  {conditionLabel(r.condition)}
                </td>

                <td style={{ padding: 12 }}>
                  <div style={{ fontWeight: 600 }}>{r.interestedEmail}</div>
                  <div className="lp2app__help">{r.interestedPostcode ?? "—"}</div>
                </td>

                <td style={{ padding: 12 }}>
                  <div style={{ fontWeight: 600 }}>{r.ownerEmail}</div>
                  <div className="lp2app__help">{r.ownerPostcode ?? "—"}</div>
                </td>

                <td style={{ padding: 12 }} className="lp2app__help">
                  {r.notes ?? "—"}
                </td>

                <td style={{ padding: 12 }} className="lp2app__help">
                  {r.status}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

function Th(props: {
  children: React.ReactNode;
  onClick?: () => void;
  active?: boolean;
  dir?: "asc" | "desc";
}) {
  const { children, onClick, active, dir } = props;
  const clickable = !!onClick;

  return (
    <th
      onClick={onClick}
      style={{
        textAlign: "left",
        padding: 12,
        userSelect: "none",
        cursor: clickable ? "pointer" : "default",
        whiteSpace: "nowrap",
        borderBottom: "1px solid rgba(255,255,255,0.12)",
        opacity: clickable ? 1 : 0.85,
      }}
      className="lp2app__help"
      title={clickable ? "Click to sort" : undefined}
    >
      <span style={{ display: "inline-flex", gap: 8, alignItems: "center" }}>
        {children}
        {active && <span>{dir === "asc" ? "▲" : "▼"}</span>}
      </span>
    </th>
  );
}

export default AdminInterests;
